import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import {
    addSearchHistory,
    getSearchHistory,
    SearchHistoryItem,
} from "@insite/client-framework/Services/AutocompleteService";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, AutocompleteModel } from "@insite/client-framework/Types/ApiModels";
import AutocompleteBrands, {
    AutocompleteBrandsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteBrands";
import AutocompleteCategories, {
    AutocompleteCategoriesStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteCategories";
import AutocompleteContent, {
    AutocompleteContentStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteContent";
import AutocompleteProducts, {
    AutocompleteProductsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteProducts";
import SearchHistory, { SearchHistoryStyles } from "@insite/content-library/Widgets/Header/SearchHistory";
import { FormFieldSizeVariant } from "@insite/mobius/FormField";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Popover, {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@insite/mobius/Popover";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { CustomAppState, xHeaderActions } from "@stoneage/App/Store/Reducers";
import { getBillToCustomerNumber, trackAsImpersonation, trackGaSearchEvent } from "@stoneage/App/Utilities/DataLayer";
import { screen } from "@stoneage/XDL/Helpers";
import { styledScrollbar } from "@stoneage/XDL/Utilities";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
    isAnimated?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
    } = getSettingsCollection(state);
    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        searchPath,
        searchInputFocus: state.xHeader.searchInputFocus,
        isSticky: state.xHeader.isSticky,
        toggleHeaderColor: state.xHeader.toggleHeaderColor,
        customerNumber: getBillToCustomerNumber(state),
        impersonation: trackAsImpersonation(state),
        searchAutoCompleteEvent: state.xGoogleAnalytics.searchAutoCompleteEvent,
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
    setSearchInputStatus: (focus: boolean) => (dispatch: Dispatch<xHeaderActions>) => {
        dispatch({ type: "xHeader/searchInputFocus", focus });
    },
    setNavDrawerIsOpen,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    query: string;
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    autoCompleteModel?: AutocompleteModel;
    isLoading: boolean;
}

const DOWN_KEY = 40;
const UP_KEY = 38;
const ENTER_KEY = 13;

export interface SearchInputStyles {
    input?: TextFieldPresentationProps;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
    searchInputWrapper?: InjectableCss;
    autocompleteColumnWrapper1?: InjectableCss;
    autocompleteColumnWrapper2?: InjectableCss;
    searchInputCtasWrapper?: InjectableCss;
}

export const searchInputStyles: SearchInputStyles = {
    input: {
        sizeVariant: "default",
        iconProps: {
            src: "IconSearch",
            css: css`
                height: 34px !important;
                padding: 6px !important;
            `,
        },
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
            formInputWrapper: css`
                margin-top: 0 !important;
                input {
                    border-radius: 0;
                    height: 37px !important;
                    background: ${getColor("common.white")} !important;

                    padding: 10px 30px 10px 11px !important;
                    min-width: 95px;

                    &:active,
                    &:focus {
                        border-color: ${getColor("common.ui7")} !important;
                    }
                }
                // XNG-changes: stop iOS zoom
                &&& {
                    ${screen("lte", "md")`
                        input {
                            font-size: 16px;
                        }
                        input::placeholder {
                            font-size: 12px;
                        }
                    `};
                }

                svg {
                    width: 20px;
                    height: 20px;
                }

                button {
                    right: 5px;
                    svg {
                        color: ${getColor("primary")};
                    }
                }
            `,
        },
    },
    popover: {
        transitionDuration: "short",
        toggle: false,
        css: css`
            box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.2);
        `,
    } as any,
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            display: inline-flex;
            justify-content: flex-end;
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
    },
    autocompleteWrapper: {
        css: css`
            display: flex;
            height: 410px;
            min-height: 350px;
            overflow-x: hidden;
            overflow-y: auto;
            ${styledScrollbar(6)};
            max-height: calc(100vh - 290px);

            ${screen("lte", "md")`
                flex-direction: column;
                max-height: 80vh;
                height: auto;
                min-height: 45vh;
            `};
        `,
    },
    autocompleteColumnWrapper1: {
        css: css`
            width: 50%;
            height: 100%;
            padding: 20px;
            position: relative;

            &:after {
                content: " ";
                position: absolute;
                height: 100%;
                width: 2px;
                background-color: rgba(0, 0, 0, 0.05);
                right: 0;
                top: 0;
            }

            &:not(.split-two-views) {
                width: 100%;
                &:after {
                    background: transparent;
                }
            }

            ${screen("lte", "md")`
                width: 100%;
                &:after {
                    width: 100%;
                    height: 2px;
                    bottom: 0;
                    top: auto;
                }
            `}
        `,
    },
    autocompleteColumnWrapper2: {
        css: css`
            width: 50%;
            padding: 20px;
            height: 100%;

            &:not(.split-two-views) {
                width: 100%;
            }

            ${screen("lte", "md")`
                width: 100%;
            `}
        `,
    },
    searchInputWrapper: {
        css: css`
            width: 100%;
        `,
    },
};

const styles = searchInputStyles;

class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
        const parsedQuery = parseQueryString<{ query?: string }>(this.props.location.search);

        this.state = {
            query: parsedQuery.query || "",
            focusableAutocompleteItems: [],
            isLoading: false,
        };
    }

    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<HTMLUListElement>();
    showAutocomplete = true;

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            if (
                this.props.location.pathname &&
                !this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)
            ) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ query: "" });
            } else {
                this.closeAutocomplete();
            }
        }
        if (
            this.state.autoCompleteModel &&
            prevState.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            this.updateFocusableItems();
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize, true);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize = () => {
        this.updateFocusableItems();
    };

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const isProductsOnTop = window.innerWidth < this.props.theme.breakpoints.values[3];
        const newFocusableOptions = isProductsOnTop
            ? (products || ([] as AutocompleteItemModel[]))
                  .concat(categories || [])
                  .concat(brands || [])
                  .concat(content || [])
            : (categories || [])
                  .concat(brands || [])
                  .concat(content || [])
                  .concat(products || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    doSearch = () => {
        if (this.state.query.trim() === "") {
            return;
        }

        this.showAutocomplete = false;
        if (this.state.autoCompleteModel?.products?.length === 1) {
            this.goToUrl(this.state.autoCompleteModel?.products[0].url);
        } else {
            const encodedQuery = encodeURIComponent(this.state.query);
            this.goToUrl(`/${this.props.searchPath}?query=${encodedQuery}`, false);

            if (this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // if we already on the Search page then scroll to top
                window.scrollTo(0, 0);
            }
        }
        if (this.props.searchHistoryEnabled) {
            addSearchHistory(this.state.query, this.props.searchHistoryLimit);
        }

        this.inputFocusStoreReset();
    };

    // XNG-Change: track GA4
    handleGaSearchEvent = () => {
        if (this.state.autoCompleteModel && this.props.searchAutoCompleteEvent) {
            const { categories, brands, content, products } = this.state.autoCompleteModel;
            const resultCount =
                (products?.length || 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
            trackGaSearchEvent(this.state.query, resultCount, this.props.impersonation, this.props.customerNumber);
        }
    };

    loadAutocomplete = debounce((query: string) => {
        this.props.getAutocompleteModel({
            query,
            onSuccess: result => {
                if (this.showAutocomplete) {
                    this.setState({
                        autoCompleteModel: result,
                    });
                    this.openAutocomplete();
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    }, 200);

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.showAutocomplete = true;
        const newQuery = event.target.value;
        this.setState({ query: newQuery });
        this.loadAutocomplete(newQuery);
    };

    openAutocomplete = () => {
        this.popover.current && (this.popover.current! as any).openPopover();
    };

    closeAutocomplete = () => {
        this.popover.current && (this.popover.current! as any).closePopover();
        this.inputFocusStoreReset();
    };

    getPopoverPosition = (element: React.RefObject<HTMLUListElement>) => {
        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        const linksSearchElement = document.getElementsByClassName("header_links-search")[0].getBoundingClientRect();
        const searchElement = document.getElementsByClassName("header_search")[0].getBoundingClientRect();
        const headerElement = document.getElementsByTagName("header")[0].getBoundingClientRect();

        const windowSize = window.innerWidth;
        if (element.current && linksSearchElement && searchElement) {
            const rect = element.current.getBoundingClientRect();
            if (windowSize >= 1025) {
                // Desktop view
                positionStyle.top =
                    rect.top +
                    (rect.height > 100
                        ? sizeVariantValues[this.styles?.input?.sizeVariant || ("default" as FormFieldSizeVariant)]
                              .height
                        : rect.height);
                positionStyle.right = document.documentElement.clientWidth - rect.right;
                positionStyle.width = `${linksSearchElement.width}px`;
                if (windowSize < 1251) {
                    positionStyle.width = `${linksSearchElement.width - 15}px`;
                }
            } else if (windowSize < 1025 && windowSize > 768) {
                // Tablet view
                positionStyle.top = headerElement.height - 6;
                positionStyle.right = document.documentElement.clientWidth - rect.right;
                positionStyle.width = `${searchElement.width}px`;
            } else {
                // Mobile view
                positionStyle.top = headerElement.height;
                positionStyle.right = 0;
                positionStyle.width = "100%";
            }
        }

        return positionStyle;
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.state.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    handlePopoverKeyDown = (event: KeyboardEvent) => {
        switch (event.keyCode) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prev");
                break;
            case ENTER_KEY:
                // eslint-disable-next-line no-case-declarations
                const searchId = `headerSearch-${this.props.id}`;
                document?.getElementById(searchId)?.blur();
                if (this.state.focusedAutocompleteItem) {
                    this.goToUrl(this.state.focusedAutocompleteItem.url);
                } else if (this.state.focusedSearchHistoryQuery) {
                    this.setState({ query: this.state.focusedSearchHistoryQuery }, () => {
                        this.doSearch();
                    });
                } else {
                    this.doSearch();
                }
                break;
            default:
                break;
        }
    };

    handlePopoverClose = () => {
        this.setState({
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
        this.inputFocusStoreReset();
    };

    trackAutcompleteSearchResults = () => {
        // Make sure only to trigger for autocomplete results
        if (!this.state.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
        if (resultCount > 0) {
            // Track an autocomplete of 1+ items
            this.props.sendSearchTracking({
                searchEvent: "autocompleteSearchResults",
                searchTerm: this.state.query,
                resultCount,
                productCount: products?.length,
                categoryCount: categories?.length,
                contentCount: content?.length,
                brandCount: brands?.length,
            });
            return;
        }

        // Track zero resultCount search results and triggers from this.doSearch
        this.props.sendSearchTracking({
            searchEvent: "searchResults",
            searchTerm: this.state.query,
            resultCount,
        });
    };

    goToUrl = (url: string, track = true) => {
        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        if (track) {
            this.trackAutcompleteSearchResults();
        }
        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        this.closeAutocomplete();
        this.setState({
            // don't use stale results on next search and go to wrong PDP
            autoCompleteModel: undefined,
        });
        this.props.history.push(url);
    };

    closeNavDrawer = () => {
        this.props.setNavDrawerIsOpen({ navDrawerIsOpen: false });
    };

    inputFocusSet = (event?: any) => {
        this.closeNavDrawer();
        this.props.setSearchInputStatus(true);
    };

    inputFocusStoreReset = () => {
        setTimeout(() => {
            this.props.setSearchInputStatus(false);
        }, 100);
    };

    closeAutoCompleteAndPopover = () => {
        this.handlePopoverClose();
        this.closeAutocomplete();
        this.inputFocusStoreReset();
        this.showAutocomplete = false;
    };

    renderAutocomplete = () => {
        if (!this.props.autocompleteEnabled) {
            return null;
        }

        if (!this.state.autoCompleteModel) {
            return null;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;

        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);

        if (!resultCount) {
            return null;
        }

        return (
            <StyledWrapper {...this.styles.autocompleteWrapper}>
                {((categories && categories.length > 0) ||
                    (brands && brands.length > 0) ||
                    (content && content.length > 0)) && (
                    <StyledWrapper
                        {...this.styles.autocompleteColumnWrapper1}
                        className={products && products.length > 0 ? "split-two-views" : ""}
                    >
                        <AutocompleteCategories
                            categories={categories}
                            focusedItem={this.state.focusedAutocompleteItem}
                            goToUrl={this.goToUrl}
                            extendedStyles={this.styles.autocompleteCategoriesStyles}
                        />
                        <AutocompleteContent
                            content={content}
                            focusedItem={this.state.focusedAutocompleteItem}
                            goToUrl={this.goToUrl}
                            extendedStyles={this.styles.autocompleteContentStyles}
                        />
                    </StyledWrapper>
                )}
                {products && products.length > 0 && (
                    <StyledWrapper
                        {...this.styles.autocompleteColumnWrapper2}
                        className={
                            (categories && categories.length > 0) ||
                            (brands && brands.length > 0) ||
                            (content && content.length > 0)
                                ? "split-two-views"
                                : ""
                        }
                    >
                        <AutocompleteProducts products={products} goToUrl={this.goToUrl} />
                    </StyledWrapper>
                )}
            </StyledWrapper>
        );
    };

    render() {
        const styles = this.styles;
        const searchId = `headerSearch-${this.props.id}`;
        // XNG-changes
        const searchInputFocus = this.props.searchInputFocus;
        const searchHistory = getSearchHistory();

        const searchInput = (
            <StyledWrapper {...({ searchInputFocus } as any)} {...styles.searchInputWrapper}>
                <TextField
                    {...styles.input}
                    ref={this.props.inputRef}
                    iconClickableProps={{
                        onClick: () => {
                            // apply focus state when clicking on search icon
                            this.inputFocusSet();
                            this.doSearch();
                        },
                        className: "clickable-icon",
                        css: css``,
                        "aria-roledescription": `Submit Search`,
                        id: `headerSearchButton`,
                    }}
                    value={this.state.query}
                    onChange={this.handleInputChange}
                    onFocus={this.inputFocusSet}
                    onBlur={this.handleGaSearchEvent}
                    placeholder={translate("Search")}
                    autoComplete="off"
                    clickableText={translate("submit search")}
                    id={searchId}
                    data-test-selector="headerSearchInputTextField"
                    className="search-input"
                    aria-multiline="false"
                    aria-required="false"
                    {...({ searchInputFocus } as any)}
                />
            </StyledWrapper>
        );

        return (
            <>
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>
                <Popover
                    {...styles.popover}
                    controlsId={`${searchId}-popover`}
                    ref={this.popover}
                    insideRefs={[this.list]}
                    popoverTrigger={searchInput}
                    positionFunction={this.getPopoverPosition}
                    wrapperProps={styles.popoverWrapper}
                    contentBodyProps={
                        {
                            ref: this.list,
                            ...styles.popoverContentBody,
                        } as any
                    }
                    handleKeyDown={this.handlePopoverKeyDown}
                    onClose={this.handlePopoverClose}
                    className="popover"
                >
                    {!this.state.query && this.showAutocomplete && searchHistory?.length > 0 && (
                        <StyledWrapper {...this.styles.autocompleteWrapper}>
                            <SearchHistory
                                focusedQuery={this.state.focusedSearchHistoryQuery}
                                goToUrl={this.goToUrl}
                                extendedStyles={styles.searchHistoryStyles}
                                {...({ closeAutoCompleteAndPopover: this.closeAutoCompleteAndPopover } as any)}
                            />
                        </StyledWrapper>
                    )}
                    {this.state.query && this.renderAutocomplete()}
                </Popover>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));
